import { createContext, useContext, useState } from "react";

const T = {
  en: {
    nav: { story: "Our Story", party: "Wedding Party", weekend: "Weekend", venue: "Venue", travel: "Travel", faq: "FAQ", registry: "Registry", rsvp: "RSVP" },
    hero: {
      overline: "Together with their families",
      married: "are getting married",
      dateline: "March 27, 2027 · Miami, Florida",
    },
    welcome: {
      overline: "Welcome",
      title: "We're so happy you're here",
      body: "This space was created to guide you through every detail as our day approaches — venue information, the weekend schedule, RSVP, and more. Having you with us means more than words can say, and we can't wait to celebrate together.",
      signoff: "With love, Paulina & Louie",
    },
    countdown: { title: "Our forever starts in", days: "days", hours: "hours", minutes: "minutes", seconds: "seconds" },
    story: {
      overline: "Our Story",
      line1: "Every love story is beautiful,",
      line2: "but ours is my favorite.",
      chapters: [
        { n: "01", title: "A Simple Hello", text: "Our story began on December 10, 2022, with a simple hello at a bar in Denver, Colorado. A girl from Monterrey, MX, and a guy from Raleigh, NC — yet somehow life brought us together in the most unexpected place. A moment we now call fate." },
        { n: "02", title: "Building a Life", text: "From that night on, we've been inseparable. Together we've built a life filled with adventure, shared dreams, and our sweet Saint Bernard, Bear, who made our little family complete." },
        { n: "03", title: "The Question", text: "In July 2025 we moved to Florida, and on November 16, 2025, he proposed — beginning the next chapter of our story." },
        { n: "04", title: "Forever", text: "Now we can't wait to celebrate our love and the start of forever with the people who mean the most to us." },
      ],
    },
    weekend: {
      overline: "The Weekend",
      title: "Three days in",
      maps: "Google Maps →",
      events: [
        { id: "friday", day: "Friday", date: "March 26, 2027", title: "Welcome Party", place: "The Globe", address: "377 Alhambra Circle, Coral Gables, FL 33134", time: "6:00 – 10:00 PM", note: "A welcome party to kick off the weekend — cocktails with the people we love most.", maps: "https://www.google.com/maps/dir/?api=1&destination=377+Alhambra+Circle,+Coral+Gables,+FL+33134" },
        { id: "saturday", day: "Saturday", date: "March 27, 2027", title: "The Wedding", place: "Loews Coral Gables Hotel", address: "2950 Coconut Grove Dr, Coral Gables, FL 33134", time: "Ceremony at 5:00 PM", note: "A rooftop ceremony above the Gables, then cocktails, dinner & dancing in the ballroom — celebrating late into the night.", maps: "https://www.google.com/maps/dir/?api=1&destination=2950+Coconut+Grove+Dr,+Coral+Gables,+FL+33134" },
        { id: "sunday", day: "Sunday", date: "March 28, 2027", title: "Farewell Brunch", place: "Coral Gables Country Club", address: "990 Alhambra Circle, Coral Gables, FL 33134", time: "Details to follow", note: "One last gathering before we all say hasta pronto.", maps: "https://www.google.com/maps/dir/?api=1&destination=990+Alhambra+Circle,+Coral+Gables,+FL+33134" },
      ],
    },
    venue: { overline: "The Venue", directions: "Get Directions", tagline: "A rooftop ceremony above the Gables, then dinner and dancing in the grand ballroom — one beautiful evening, all under one roof." },
    travel: {
      overline: "Travel & Stay",
      title: "Getting to",
      notice: "Heads up: our wedding falls on a busy music-festival weekend in Miami. Flights, hotels, and rental cars fill up fast — we recommend booking everything as early as you can.",
      cards: [
        { title: "Getting Here", lines: ["Miami International Airport (MIA) — about 15 minutes from Coral Gables.", "Fort Lauderdale (FLL) — a good alternative, about an hour away.", "Ride-shares are reliable throughout Miami; a rental car is handy if you plan to explore."] },
        {
          title: "Where to Stay",
          lines: ["We have room blocks at two Coral Gables hotels:", "The Loews Coral Gables is also home to our Saturday celebration — stay where the wedding is."],
          links: [
            { label: "Book the Loews Coral Gables room block →", url: "https://www.loewshotels.com/coral-gables/group-diana-paulina-anthony-luis-wedding-room-block" },
            { label: "Book the Aloft Coral Gables room block →", url: "https://www.marriott.com/event-reservations/reservation-link.mi?id=1784139859933&key=GRP&app=resvlink" },
          ],
        },
        { title: "Things to Do", lines: ["Wander the streets and cafecito windows of Little Havana.", "Art Deco strolls and beach mornings in South Beach.", "The Wynwood Walls & Design District murals.", "Vizcaya Museum & Gardens and Fairchild Tropical Botanic Garden — both near the venue.", "The Venetian Pool and a stroll past the Biltmore in Coral Gables."] },
        { title: "Where to Eat", lines: ["Versailles — the legendary Cuban classic in Little Havana.", "Cafe La Trova — award-winning cocktails and Cuban fare.", "Bulla Gastrobar — lively Spanish tapas in Coral Gables.", "Joe's Stone Crab — the South Beach institution.", "La Mar by Gastón Acurio — Peruvian with a waterfront view."] },
      ],
    },
    rsvp: {
      overline: "RSVP",
      title: "Our day wouldn't be the same",
      titleAccent: "without you.",
      deadlinePre: "Kindly respond by",
      deadlineDate: "February 1, 2027",
      name: "Full Name *",
      namePh: "Your name",
      email: "Email *",
      join: "Will you join us?",
      accept: "Joyfully accepts",
      decline: "Regretfully declines",
      guests: "Number of Guests",
      meal: "Meal Preference",
      meals: [["beef", "Beef"], ["fish", "Fish"], ["vegetarian", "Vegetarian"]],
      events: "Which events will you attend?",
      eventOptions: [["welcome_party", "Friday · Welcome Party"], ["wedding", "Saturday · The Wedding"], ["brunch", "Sunday · Farewell Brunch"]],
      dietary: "Dietary Restrictions",
      dietaryPh: "Allergies, preferences…",
      song: "Song Request",
      songPh: "What gets you dancing?",
      note: "A note for the couple",
      notePh: "Optional",
      submit: "Send RSVP",
      sending: "Sending…",
      successTitle: "Thank you!",
      successYes: "We can't wait to celebrate with you in Miami.",
      successNo: "We'll miss you — thank you for letting us know.",
      toastSuccess: "RSVP received — thank you!",
      toastError: "Something went wrong. Please try again.",
      toastValidation: "Please add your name and email.",
      findTitle: "Find your invitation",
      findPh: "Type your first & last name…",
      findBtn: "Find",
      seats: "seats reserved",
      notFound: "We couldn't find that name — please try another spelling, or reach out to us directly.",
      generalLink: "Continue without invitation lookup",
      changeParty: "Change",
      whoAttending: "Who will be attending?",
      noAttendee: "Please select at least one guest.",
      addGuest: "Add a guest (+1)",
      guestNamePh: "Guest name…",
    },
    party: {
      overline: "The Wedding Party",
      title: "The ones standing",
      titleAccent: "beside us",
      herSide: "By her side",
      hisSide: "By his side",
      her: [
        { name: "Isabella Rascon", role: "Maid of Honor" },
        { name: "Megan Amaru", role: "Bridesmaid" },
        { name: "Diana Murillo", role: "Bridesmaid" },
        { name: "Alessandra Rodriguez", role: "Bridesmaid" },
        { name: "Victoria Gutierrez", role: "Bridesmaid" },
        { name: "Sofia Heredia", role: "Bridesmaid" },
        { name: "Camila McCord", role: "Bridesmaid" },
      ],
      him: [
        { name: "Ty Giggey", role: "Best Man" },
        { name: "Kyle Fitzgerald", role: "Groomsman" },
        { name: "Anish Jagath", role: "Groomsman" },
        { name: "Matt Zachek", role: "Groomsman" },
        { name: "Eric Anderson", role: "Groomsman" },
        { name: "Steven Anderson", role: "Groomsman" },
      ],
      bear: { name: "Bear", role: "Official Ring Bearer", blurb: "170 pounds of pure love. May nap during the ceremony." },
      alexa: { name: "Alexa Rascon", role: "Junior Maid of Honor", blurb: "The bride's little sister, on official duty walking Bear down the aisle." },
    },
    faq: {
      overline: "Questions",
      title: "Good to",
      titleAccent: "know",
      items: [
        { q: "What time should I arrive?", a: "The ceremony begins at 5:00 PM. We kindly ask guests to arrive by 4:30 PM to allow time to get settled." },
        { q: "Is transportation provided?", a: "The wedding takes place at the Loews Coral Gables — if you stay there, the celebration is just an elevator ride away. Transportation details for everyone else will follow." },
        { q: "Are children welcome?", a: "While we love your little ones, we have chosen to make our wedding an adults-only celebration." },
        { q: "What is the dress code for the wedding?", a: "The dress code for our wedding is Formal Attire. Ladies are encouraged to wear floor-length gowns, elegant midi dresses, or formal cocktail dresses. Gentlemen are asked to wear a suit or tuxedo." },
        { q: "Will there be a welcome party?", a: "Yes! Join us Friday at The Globe in Coral Gables from 6:00 to 10:00 PM." },
        { q: "What is the dress code for the welcome party?", a: "Chic and elevated casual for our welcome party at The Globe." },
        { q: "When is the RSVP deadline?", a: "Please RSVP by February 1, 2027 so we can finalize all details accordingly." },
        { q: "Is parking available?", a: "Yes — parking and valet are available at the Loews Coral Gables, and our Friday and Sunday venues are just minutes away with parking nearby." },
        { q: "What is the best airport to fly into?", a: "We recommend flying into Miami International Airport (MIA), which is closest to the venue and accommodations." },
      ],
    },
    registry: {
      overline: "Registry",
      title: "Your presence is the",
      titleAccent: "greatest gift",
      body: "Having you celebrate with us in Miami means everything. Should you wish to honor us with a gift, a contribution toward our honeymoon and first home together would be deeply appreciated.",
      button: "Honeymoon Fund",
    },
    banner: {
      title: "Don't forget —",
      text: "kindly RSVP by February 1. Only {days} days left!",
      textNoDays: "kindly RSVP by February 1.",
      cta: "RSVP Now",
    },
    gallery: {
      overline: "Moments",
      title: "A few of our",
      titleAccent: "favorites",
    },
    addToCalendar: "Add to calendar:",
    footer: { quote: "Every love story is beautiful, but ours is my favorite." },
  },
  es: {
    nav: { story: "Nuestra Historia", party: "Cortejo Nupcial", weekend: "Fin de Semana", venue: "El Lugar", travel: "Viaje", faq: "Preguntas", registry: "Mesa de Regalos", rsvp: "RSVP" },
    hero: {
      overline: "Junto a sus familias",
      married: "se casan",
      dateline: "27 de marzo de 2027 · Miami, Florida",
    },
    welcome: {
      overline: "Bienvenidos",
      title: "Nos alegra muchísimo que estés aquí",
      body: "Este espacio se creó para guiarte a través de todos los detalles a medida que se acerca nuestro gran día — información del lugar, el programa del fin de semana, confirmación de asistencia y mucho más. Que estés con nosotros significa muchísimo, y no podemos esperar para celebrar juntos.",
      signoff: "Con cariño, Paulina & Louie",
    },
    countdown: { title: "Nuestro para siempre comienza en", days: "días", hours: "horas", minutes: "minutos", seconds: "segundos" },
    story: {
      overline: "Nuestra Historia",
      line1: "Cada historia de amor es hermosa,",
      line2: "pero la nuestra es mi favorita.",
      chapters: [
        { n: "01", title: "Un Simple Saludo", text: "Nuestra historia comenzó el 10 de diciembre de 2022 con un simple saludo en un bar de Denver, Colorado. Una chica de Monterrey, MX, y un chico de Raleigh, NC — sin embargo, la vida nos unió en el lugar más inesperado. Un momento que ahora llamamos destino." },
        { n: "02", title: "Construyendo una Vida", text: "Desde esa noche hemos sido inseparables. Juntos hemos construido una vida llena de aventuras, sueños compartidos y nuestro dulce San Bernardo, Bear, que completó nuestra pequeña familia." },
        { n: "03", title: "La Pregunta", text: "En julio de 2025 nos mudamos a Florida, y el 16 de noviembre de 2025, él le propuso matrimonio — dando inicio al siguiente capítulo de nuestra historia." },
        { n: "04", title: "Para Siempre", text: "Ahora no podemos esperar para celebrar nuestro amor y el comienzo de nuestro para siempre con las personas que más queremos." },
      ],
    },
    weekend: {
      overline: "El Fin de Semana",
      title: "Tres días en",
      maps: "Google Maps →",
      events: [
        { id: "friday", day: "Viernes", date: "26 de marzo de 2027", title: "Fiesta de Bienvenida", place: "The Globe", address: "377 Alhambra Circle, Coral Gables, FL 33134", time: "6:00 – 10:00 PM", note: "Una fiesta de bienvenida para arrancar el fin de semana — una noche de cócteles con la gente que más queremos.", maps: "https://www.google.com/maps/dir/?api=1&destination=377+Alhambra+Circle,+Coral+Gables,+FL+33134" },
        { id: "saturday", day: "Sábado", date: "27 de marzo de 2027", title: "La Boda", place: "Loews Coral Gables Hotel", address: "2950 Coconut Grove Dr, Coral Gables, FL 33134", time: "Ceremonia a las 5:00 PM", note: "Ceremonia en la terraza con vista a Coral Gables, seguida de cócteles, cena y baile en el salón de fiestas — celebrando hasta altas horas de la noche.", maps: "https://www.google.com/maps/dir/?api=1&destination=2950+Coconut+Grove+Dr,+Coral+Gables,+FL+33134" },
        { id: "sunday", day: "Domingo", date: "28 de marzo de 2027", title: "Brunch de Despedida", place: "Coral Gables Country Club", address: "990 Alhambra Circle, Coral Gables, FL 33134", time: "Detalles próximamente", note: "Una última reunión antes de decir hasta pronto.", maps: "https://www.google.com/maps/dir/?api=1&destination=990+Alhambra+Circle,+Coral+Gables,+FL+33134" },
      ],
    },
    venue: { overline: "El Lugar", directions: "Cómo Llegar", tagline: "Una ceremonia en la terraza con vista a Coral Gables, seguida de cena y baile en el gran salón — una noche hermosa, todo bajo un mismo techo." },
    travel: {
      overline: "Viaje y Hospedaje",
      title: "Llegando a",
      notice: "Aviso: nuestra boda coincide con un fin de semana de festival de música muy concurrido en Miami. Los vuelos, hoteles y autos de renta se agotan rápido — te recomendamos reservar todo lo antes posible.",
      cards: [
        { title: "Cómo Llegar", lines: ["Aeropuerto Internacional de Miami (MIA) — a unos 15 minutos de Coral Gables.", "Fort Lauderdale (FLL) — una buena alternativa, a una hora de distancia.", "Uber y Lyft funcionan muy bien en Miami; un auto rentado es útil si planeas explorar."] },
        {
          title: "Dónde Hospedarse",
          lines: ["Tenemos bloques de habitaciones en dos hoteles de Coral Gables:", "El Loews Coral Gables también es la sede de nuestra celebración del sábado — quédate donde será la boda."],
          links: [
            { label: "Reserva en el bloque del Loews Coral Gables →", url: "https://www.loewshotels.com/coral-gables/group-diana-paulina-anthony-luis-wedding-room-block" },
            { label: "Reserva en el bloque del Aloft Coral Gables →", url: "https://www.marriott.com/event-reservations/reservation-link.mi?id=1784139859933&key=GRP&app=resvlink" },
          ],
        },
        { title: "Qué Hacer", lines: ["Recorre las calles y ventanitas de cafecito de La Pequeña Habana.", "Paseos Art Déco y mañanas de playa en South Beach.", "Los murales de Wynwood y el Design District.", "El Museo Vizcaya y el Jardín Botánico Fairchild — ambos cerca del lugar.", "La Venetian Pool y un paseo por el Biltmore en Coral Gables."] },
        { title: "Dónde Comer", lines: ["Versailles — el clásico cubano legendario en La Pequeña Habana.", "Cafe La Trova — cócteles premiados y cocina cubana.", "Bulla Gastrobar — tapas españolas en Coral Gables.", "Joe's Stone Crab — la institución de South Beach.", "La Mar de Gastón Acurio — peruano frente al mar."] },
      ],
    },
    rsvp: {
      overline: "Confirmación de Asistencia",
      title: "Nuestro día no sería igual",
      titleAccent: "sin ti.",
      deadlinePre: "Por favor confirma antes del",
      deadlineDate: "1 de febrero de 2027",
      name: "Nombre Completo *",
      namePh: "Tu nombre",
      email: "Correo Electrónico *",
      join: "¿Nos acompañarás?",
      accept: "Acepta con alegría",
      decline: "Declina con pesar",
      guests: "Número de Invitados",
      meal: "Preferencia de Menú",
      meals: [["beef", "Res"], ["fish", "Pescado"], ["vegetarian", "Vegetariano"]],
      events: "¿A qué eventos asistirás?",
      eventOptions: [["welcome_party", "Viernes · Fiesta de Bienvenida"], ["wedding", "Sábado · La Boda"], ["brunch", "Domingo · Brunch de Despedida"]],
      dietary: "Restricciones Alimentarias",
      dietaryPh: "Alergias, preferencias…",
      song: "Pide una Canción",
      songPh: "¿Qué te pone a bailar?",
      note: "Una nota para los novios",
      notePh: "Opcional",
      submit: "Enviar RSVP",
      sending: "Enviando…",
      successTitle: "¡Gracias!",
      successYes: "No podemos esperar para celebrar contigo en Miami.",
      successNo: "Te extrañaremos — gracias por avisarnos.",
      toastSuccess: "¡RSVP recibido — gracias!",
      toastError: "Algo salió mal. Inténtalo de nuevo.",
      toastValidation: "Por favor agrega tu nombre y correo.",
      findTitle: "Encuentra tu invitación",
      findPh: "Escribe tu nombre y apellido…",
      findBtn: "Buscar",
      seats: "lugares reservados",
      notFound: "No encontramos ese nombre — intenta con otra escritura o contáctanos directamente.",
      generalLink: "Continuar sin buscar invitación",
      changeParty: "Cambiar",
      whoAttending: "¿Quiénes asistirán?",
      noAttendee: "Selecciona al menos un invitado.",
      addGuest: "Agregar acompañante (+1)",
      guestNamePh: "Nombre del acompañante…",
    },
    party: {
      overline: "El Cortejo",
      title: "Quienes estarán",
      titleAccent: "a nuestro lado",
      herSide: "Por ella",
      hisSide: "Por él",
      her: [
        { name: "Isabella Rascon", role: "Dama de Honor" },
        { name: "Megan Amaru", role: "Dama" },
        { name: "Diana Murillo", role: "Dama" },
        { name: "Alessandra Rodriguez", role: "Dama" },
        { name: "Victoria Gutierrez", role: "Dama" },
        { name: "Sofia Heredia", role: "Dama" },
        { name: "Camila McCord", role: "Dama" },
      ],
      him: [
        { name: "Ty Giggey", role: "Best Man" },
        { name: "Kyle Fitzgerald", role: "Caballero" },
        { name: "Anish Jagath", role: "Caballero" },
        { name: "Matt Zachek", role: "Caballero" },
        { name: "Eric Anderson", role: "Caballero" },
        { name: "Steven Anderson", role: "Caballero" },
      ],
      bear: { name: "Bear", role: "Portador Oficial de los Anillos", blurb: "77 kilos de puro amor. Puede que tome una siesta durante la ceremonia." },
      alexa: { name: "Alexa Rascon", role: "Dama de Honor Junior", blurb: "La hermana menor de la novia, con la misión oficial de llevar a Bear al altar." },
    },
    faq: {
      overline: "Preguntas",
      title: "Lo que debes",
      titleAccent: "saber",
      items: [
        { q: "¿A qué hora debo llegar?", a: "La ceremonia comienza a las 5:00 PM. Les rogamos a los invitados que lleguen antes de las 4:30 PM para que tengan tiempo de acomodarse." },
        { q: "¿Se proporciona transporte?", a: "La boda será en el Loews Coral Gables — si te hospedas ahí, la celebración está a solo un paseo en elevador. Pronto compartiremos detalles de transporte para los demás invitados." },
        { q: "¿Son bienvenidos los niños?", a: "Aunque adoramos a sus pequeños, hemos decidido que nuestra boda sea una celebración exclusiva para adultos." },
        { q: "¿Cuál es el código de vestimenta para la boda?", a: "El código de vestimenta para nuestra boda es Formal. Invitamos a las damas a usar vestidos largos, vestidos midi elegantes o vestidos formales de cóctel. Los caballeros deberán vestir traje o esmoquin." },
        { q: "¿Habrá fiesta de bienvenida?", a: "¡Sí! Acompáñanos el viernes en The Globe, en Coral Gables, de 6:00 a 10:00 PM." },
        { q: "¿Cuál es el código de vestimenta para la fiesta de bienvenida?", a: "Estilo chic y casual elegante para nuestra fiesta de bienvenida en The Globe." },
        { q: "¿Cuál es la fecha límite para confirmar asistencia?", a: "Por favor confirma tu asistencia antes del 1 de febrero de 2027 para poder finalizar todos los detalles." },
        { q: "¿Habrá estacionamiento disponible?", a: "Sí — el Loews Coral Gables cuenta con estacionamiento y valet, y nuestros lugares del viernes y domingo están a pocos minutos con estacionamiento cercano." },
        { q: "¿Cuál es el mejor aeropuerto para llegar?", a: "Recomendamos volar al Aeropuerto Internacional de Miami (MIA), que es el más cercano al lugar del evento y los hoteles." },
      ],
    },
    registry: {
      overline: "Mesa de Regalos",
      title: "Tu presencia es el",
      titleAccent: "mejor regalo",
      body: "Tenerte celebrando con nosotros en Miami lo es todo. Si deseas honrarnos con un regalo, una contribución para nuestra luna de miel y nuestro primer hogar juntos sería profundamente apreciada.",
      button: "Fondo de Luna de Miel",
    },
    banner: {
      title: "No lo olvides —",
      text: "por favor confirma antes del 1 de febrero. ¡Solo quedan {days} días!",
      textNoDays: "por favor confirma antes del 1 de febrero.",
      cta: "Confirmar",
    },
    gallery: {
      overline: "Momentos",
      title: "Algunos de nuestros",
      titleAccent: "favoritos",
    },
    addToCalendar: "Agregar al calendario:",
    footer: { quote: "Cada historia de amor es hermosa, pero la nuestra es mi favorita." },
  },
};

const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("pl-lang");
    if (saved) return saved;
    return (navigator.language || "").toLowerCase().startsWith("es") ? "es" : "en";
  });
  const toggle = () =>
    setLang((l) => {
      const next = l === "en" ? "es" : "en";
      localStorage.setItem("pl-lang", next);
      return next;
    });
  return <LangContext.Provider value={{ lang, toggle, c: T[lang] }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
